import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Table, Dropdown, Input, Button, Popup } from 'semantic-ui-react';
import agent from '../../../../app/api/agent';
import { useStore } from '../../../../app/stores/store';
import { formatLocalTimeToISOString, statusOptions } from './TimeSheet';
import { toast } from 'react-toastify';

const NFAITimeSheet = () => {
  const { userStore: { user } } = useStore();
  const [startReel, setStartReel] = useState<number>(0);
  const [endReel, setEndReel] = useState<number>(0);
  const [compReel, setCompReel] = useState<number>(0);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date());
  const [duration, setDuration] = useState<string>('00:00');
  const [status, setStatus] = useState<string>('');
  const [scopeOfWork, setScopeOfWork] = useState<string>('');
  const [rejectionMinutes, setRejectionMinutes] = useState<string>('00:00:00');
  const [noOfCorrections, setNoOfCorrections] = useState<number>(0);
  const [typesOfCorrection, setTypesOfCorrection] = useState<string>('');
  const [scopeList, setScopeList] = useState<{ id: string; name: string; value: string }[]>([]);
  const [remarks, setRemarks] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<{ id: string; name: string; value: string }[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false); 
  const [editRow, setEditRow] = useState<any>(null);
  const [, setEditingRowId] = useState<string | null>(null); 
  const department = user?.departmentName as string;
  const empId = user?.displayName as string;
  const isProjectSelected = selectedProject !== '';
  const isScopeSelected = scopeOfWork !== '';
  const isStatusSelected = status !== '';
  const isStartReel = startReel !== 0;
  const isEndReel = endReel !== 0;
  const [endReelError, setEndReelError] = useState<string | null>(null);
  const [endTimeError, setEndTimeError] = useState<string | null>(null);
  const [titleLoader, settitleLoader] = useState<boolean>(false);

  const resetForm = () => {
    setStartReel(0);
    setEndReel(0);
    setCompReel(0);
    setStartTime(new Date());
    setEndTime(new Date());
    setDuration('00:00');
    setStatus('');
    setScopeOfWork('');
    setRemarks('');
    setRejectionMinutes('00:00:00');
    setNoOfCorrections(0);
    setTypesOfCorrection('');
  };

  useEffect(() => {
    if (startReel !== 0 && endReel !== 0 && !isNaN(startReel) && !isNaN(endReel)) {
      const reelDiff = (endReel - startReel) + 1;
      setCompReel(reelDiff);
    } else {
      setCompReel(0);
    }
  }, [startReel, endReel]);

  useEffect(() => {
    if (startTime && endTime) {
      const diffInMilliseconds = endTime.getTime() - startTime.getTime();
      if (diffInMilliseconds > 0) {
        const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);
        const minutes = Math.floor((diffInMilliseconds / 1000 / 60) % 60);
        setDuration(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
      } else {
        setDuration('00:00');
      }
    }
  }, [startTime, endTime]);

  useEffect(() => {
    if (endReel < startReel && endReel !== 0) {
      setEndReelError('End reel must be greater than start reel.');
    } else {
      setEndReelError(null);
    }

    if (endTime.getTime() <= startTime.getTime() && isProjectSelected) {
      setEndTimeError('End time must be greater than start time.');
    } else {
      setEndTimeError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endReel, endTime, startTime]);

  useEffect(() => {
    if (!department) return;  
    const fetchScopeList = async (department: string) => {
      try {
        const { data: list } = await agent.NFAIRestoTimeSheet.getScopeOfWorkList(department);
        const loadScopeList = list.map((item: any) => ({
          id: item.id,
          name: item.value,
          value: item.value,
        }));
        setScopeList(loadScopeList);
      } catch (error) {
        console.error("Error fetching scope list:", error);
      }
    };

    fetchScopeList(department);
  }, [department]);

  useEffect(() => {
    const fetchProjectList = async () => {
      try {
        settitleLoader(true);
        const { data: list } = await agent.NFAIRestoTimeSheet.getProjectList();
        const loadProjectList = list.map((item: any) => ({
          id: item.id,
          name: item.name,
          value: item.name,
        }));
        setProjectList(loadProjectList);
        settitleLoader(false);
      } catch (error) {
        settitleLoader(false);
        console.error("Error fetching project list:", error);
      }
    };

    fetchProjectList();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    const fetchEntriesList = async (id: string, department: string) => {
      try {
        const { data: list } = await agent.NFAIRestoTimeSheet.getTimeSheetEntries(id,department);
        setRows(list);
      } catch (error) {
        console.error("Error fetching entries list:", error);
      }
    };

    fetchEntriesList(selectedProject,department);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const handleStartTimeChange = (date: Date) => {
    setStartTime(date);
    setEndTime(new Date(date.getTime() + 60000));
  };

  const handleRejectionMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value && value.split(':').length === 2) {
      value = `${value}:00`;
    }
    if (!value) {
      value = '00:00:00';
    }
    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(value)) {
      setRejectionMinutes(value);
    } else {
      setRejectionMinutes(prev => prev || '00:00:00');
    }
  };

  const handleAddRow = async () => {
    const selectedProjectName = projectList.find((val) => val.id === selectedProject)?.name;
    if (!selectedProjectName) return toast.error('Selected project name is not available.');

    const empId = user?.displayName || 'Unknown User';
    const dept = department || 'Unknown Department';
  
    const newRow = {
      projectID: selectedProject,
      project_Name: selectedProjectName,
      emp_id: empId,
      department: dept,
      start_Reel: startReel,
      end_Reel: endReel,
      comp_Reel: compReel,
      start_Time: formatLocalTimeToISOString(startTime),
      end_Time: formatLocalTimeToISOString(endTime),
      duration: duration,
      scope_of_Work: scopeOfWork,
      status: status,
      remarks: remarks,
      rejection_Minutes: rejectionMinutes,
      no_of_Corrections: noOfCorrections,
      types_of_Correction: typesOfCorrection,
    };
  
    try {
      await agent.NFAIRestoTimeSheet.saveTimeSheet(newRow);
      toast.success('Timesheet entry added successfully');
      const { data: list } = await agent.NFAIRestoTimeSheet.getTimeSheetEntries(selectedProject, department);
      setRows(list);
      resetForm();
    } catch (error) {
      toast.error('Error saving timesheet entry');
      console.error('Error saving timesheet entry:', error);
    }
  };
  
  const handleEditRow = (row: any) => {
    setIsEditing(true);
    setEditingRowId(row.id);
    setEditRow(row);
    setStartReel(row.start_Reel);
    setEndReel(row.end_Reel);
    setCompReel(row.comp_Reel);
    setStartTime(new Date(row.start_Time));
    setEndTime(new Date(row.end_Time));
    setDuration(row.duration);
    setScopeOfWork(row.scope_of_Work);
    setStatus(row.status);
    setRemarks(row.remarks);
    setRejectionMinutes(row.rejection_Minutes);
    setNoOfCorrections(row.no_of_Corrections);
    setTypesOfCorrection(row.types_of_Correction);
  };

  const handleSaveRow = async () => {
    const updatedRow = {
      ...editRow,
      start_Reel: startReel,
      end_Reel: endReel,
      comp_Reel: compReel,
      start_Time: formatLocalTimeToISOString(startTime),
      end_Time: formatLocalTimeToISOString(endTime),
      duration: duration,
      scope_of_Work: scopeOfWork,
      status: status,
      remarks: remarks,
      rejection_Minutes: rejectionMinutes,
      no_of_Corrections: noOfCorrections,
      types_of_Correction: typesOfCorrection,
    };

    try {
      await agent.NFAIRestoTimeSheet.updateTimeSheet(updatedRow);
      toast.success('Timesheet entry updated successfully');
      setRows(rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      resetForm();
      setIsEditing(false);
      setEditingRowId(null);
      setEditRow(null);
    } catch (error) {
      toast.error('Error updating timesheet entry');
    }
  };

  const handleCancelRow = () => {
    setIsEditing(false);
    resetForm();
  }

  const handleDeleteRow = async (rowId: string) => {
    if (!rowId) {
      toast.error('Invalid row ID');
      return;
    }
    try {
      await agent.NFAIRestoTimeSheet.deleteTimeSheet(rowId);
      toast.success('Timesheet entry deleted successfully');
      const { data: list } = await agent.NFAIRestoTimeSheet.getTimeSheetEntries(selectedProject, department);
      setRows(list);
    } catch (error) {
      toast.error('Error deleting timesheet entry');
    }
  };

  return (
    <div style={{ padding: '25px', transform: 'scale(0.85)', transformOrigin: 'top left' }}>
      <Dropdown
        placeholder="Select Project Title"
        selection
        search
        clearable
        options={projectList.map((project) => ({
          key: project.id,
          value: project.id,
          text: project.name,
        }))}
        loading={titleLoader}
        value={selectedProject}
        onChange={(e, { value }) => setSelectedProject(value as string)}
      />
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Employee ID</Table.HeaderCell>
            <Table.HeaderCell>Start Reel</Table.HeaderCell>
            <Table.HeaderCell>End Reel</Table.HeaderCell>
            <Table.HeaderCell>Comp Reel</Table.HeaderCell>
            <Table.HeaderCell>Start time</Table.HeaderCell>
            <Table.HeaderCell>End time</Table.HeaderCell>
            <Table.HeaderCell>Duration</Table.HeaderCell>
            <Table.HeaderCell>Scope of work</Table.HeaderCell>
            <Table.HeaderCell>Rejection Minutes</Table.HeaderCell>
            <Table.HeaderCell>No of Corrections</Table.HeaderCell>
            <Table.HeaderCell>Types of Correction</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Remarks</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {selectedProject && rows.map((row, index) => (
            <Table.Row key={index}>
              {isEditing && editRow?.id === row.id ? (
                <>
                  <Table.Cell>{empId}</Table.Cell>
                  <Table.Cell>
                    <Input
                      value={startReel}
                      onChange={(e) => setStartReel(parseInt(e.target.value, 10))}
                      style={{width: '65px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      value={endReel}
                      onChange={(e) => setEndReel(parseInt(e.target.value, 10))}
                      style={{width: '65px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input value={compReel} readOnly style={{width: '65px'}} />
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{width: '300px'}}>
                      <DatePicker
                        selected={startTime}
                        onChange={(date) => setStartTime(date as Date)}
                        showTimeSelect
                        timeIntervals={5}
                        minDate={new Date(new Date().setDate(new Date().getDate() - 3))}
                        dateFormat="dd-MM-yyyy hh:mm aa"
                        customInput={<Input />}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{width: '300px'}}>
                      <DatePicker
                        selected={endTime}
                        onChange={(date) => setEndTime(date as Date)}
                        showTimeSelect
                        timeIntervals={5}
                        minDate={startTime}
                        dateFormat="dd-MM-yyyy hh:mm aa"
                        customInput={<Input />}
                      />
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Input value={duration} readOnly style={{width: '65px'}} />
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown
                      placeholder="Select Scope of Work"
                      fluid
                      search
                      selection
                      clearable
                      options={scopeList.map((scope) => ({
                        key: scope.id,
                        value: scope.value,
                        text: scope.name,
                      }))}
                      value={scopeOfWork}
                      onChange={(e, { value }) => setScopeOfWork(value as string)}
                      style={{width: '175px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      value={rejectionMinutes}
                      onChange={handleRejectionMinutesChange}
                      placeholder="hh:mm:ss"
                      type='time'
                      step={1}
                      style={{width: '100px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      value={noOfCorrections}
                      onChange={(e) => setNoOfCorrections(parseInt(e.target.value, 10))}
                      style={{width: '65px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      value={typesOfCorrection}
                      onChange={(e) => setTypesOfCorrection(e.target.value)}
                      style={{width: '150px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown
                      placeholder="Select Status"
                      fluid
                      selection
                      search
                      clearable
                      options={statusOptions}
                      value={status || ''}
                      onChange={(e, { value }) => setStatus(value as string)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Input
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      style={{width: '350px'}}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button.Group>
                      <Button primary onClick={handleSaveRow}>Save</Button>
                      <Button.Or />
                      <Button negative onClick={handleCancelRow}>Cancel</Button>
                    </Button.Group>
                  </Table.Cell>
                </>
              ) : (
                <>
                  <Table.Cell>{row.emp_id}</Table.Cell>
                  <Table.Cell>{row.start_Reel}</Table.Cell>
                  <Table.Cell>{row.end_Reel}</Table.Cell>
                  <Table.Cell>{row.comp_Reel}</Table.Cell>
                  <Table.Cell>{new Date(row.start_Time).toLocaleString()}</Table.Cell>
                  <Table.Cell>{new Date(row.end_Time).toLocaleString()}</Table.Cell>
                  <Table.Cell>{row.duration}</Table.Cell>
                  <Table.Cell>{row.scope_of_Work}</Table.Cell>
                  <Table.Cell>{row.rejection_Minutes}</Table.Cell>
                  <Table.Cell>{row.no_of_Corrections}</Table.Cell>
                  <Table.Cell>{row.types_of_Correction}</Table.Cell>
                  <Table.Cell>{row.status}</Table.Cell>
                  <Table.Cell>{row.remarks}</Table.Cell>
                  <Table.Cell>
                    <Button.Group>
                      <Button positive onClick={() => handleEditRow(row)}>Edit</Button>
                      <Button.Or />
                      <Button negative onClick={() => handleDeleteRow(row.id)}>Delete</Button>
                    </Button.Group>
                  </Table.Cell>
                </>
              )}
            </Table.Row>
          ))}
          {!isEditing && (
            <Table.Row>
              <Table.Cell>{empId}</Table.Cell>
              <Table.Cell>
                <Input
                  value={startReel}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsedValue = value === '' ? 0 : parseInt(value, 10);
                    setStartReel(parsedValue);
                  }}                     
                  placeholder="Enter Start Reel"
                  disabled={!isProjectSelected}
                  style={{width: '65px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Popup
                  content={endReelError}
                  trigger={
                    <Input
                      value={endReel}
                      onChange={(e) => {
                        const value = e.target.value;
                        const parsedValue = value === '' ? 0 : parseInt(value, 10);
                        setEndReel(parsedValue);
                      }}                     
                      placeholder="Enter End Reel"
                      error={!!endReelError}
                      disabled={!isProjectSelected}
                      style={{width: '65px'}}
                    />
                  }
                  position="right center"
                  size='mini'
                  open={!!endReelError}
                />
              </Table.Cell>
              <Table.Cell>
                <Input value={compReel} readOnly style={{width: '65px'}} />
              </Table.Cell>
              <Table.Cell>
              <div style={{ width: '300px' }}>
              <DatePicker
                selected={startTime instanceof Date && !isNaN(startTime.getTime()) ? startTime : new Date()}
                onChange={handleStartTimeChange}
                showTimeSelect
                timeIntervals={5}
                minDate={new Date(new Date().setDate(new Date().getDate() - 3))}
                dateFormat="dd-MM-yyyy HH:mm"
                customInput={<Input />}
                disabled={!isProjectSelected}
              />              
              </div>
              </Table.Cell>
              <Table.Cell >
              <Popup
                content={endTimeError}
                trigger={
                  <div style={{ width: '300px' }}>
                    <DatePicker
                      selected={endTime instanceof Date && !isNaN(endTime.getTime()) ? endTime : new Date()}
                      onChange={(date) => setEndTime(date as Date)}
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="dd-MM-yyyy HH:mm"
                      customInput={<Input />}
                      minDate={startTime}
                      minTime={new Date(startTime.getTime() + 60000)}
                      maxTime={new Date(startTime.getTime() + 12 * 60 * 60 * 1000)}
                      filterTime={(time)=> time > startTime}
                      disabled={!isProjectSelected}
                    />
                  </div>
                }
                position="right center"
                size="mini"
                open={!!endTimeError}
              />              
              </Table.Cell>
              <Table.Cell>
                <Input value={duration} readOnly style={{width: '75px'}} />
              </Table.Cell>
              <Table.Cell>
                <Dropdown
                  placeholder="Select Scope of Work"
                  fluid
                  selection
                  search
                  clearable
                  options={scopeList.map((scope) => ({
                    key: scope.id,
                    value: scope.value,
                    text: scope.name,
                  }))}
                  value={scopeOfWork}
                  onChange={(e, { value }) => setScopeOfWork(value as string)}
                  disabled={!isProjectSelected}
                  style={{width: '175px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Input
                  value={rejectionMinutes}
                  onChange={handleRejectionMinutesChange}
                  placeholder="hh:mm:ss"
                  type='time'
                  step={1}
                  disabled={!isProjectSelected}
                  style={{width: '100px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Input
                  value={noOfCorrections}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsedValue = value === '' ? 0 : parseInt(value, 10);
                    setNoOfCorrections(parsedValue);
                  }}                  
                  disabled={!isProjectSelected}
                  style={{width: '65px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Input
                  value={typesOfCorrection}
                  onChange={(e) => setTypesOfCorrection(e.target.value)}
                  disabled={!isProjectSelected}
                  style={{width: '150px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Dropdown
                  placeholder="Select Status"
                  fluid
                  selection
                  search
                  clearable
                  options={statusOptions}
                  value={status || ''}
                  onChange={(e, { value }) => setStatus(value as string)}
                  disabled={!isProjectSelected}
                />
              </Table.Cell>
              <Table.Cell>
                <Input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks"
                  disabled={!isProjectSelected}
                  style={{width: '350px'}}
                />
              </Table.Cell>
              <Table.Cell>
                <Button
                  primary
                  onClick={handleAddRow}
                  disabled={!isProjectSelected || !isScopeSelected || !isStatusSelected || !isStartReel || !isEndReel} 
                >
                  Add
                </Button>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );
};

export default observer(NFAITimeSheet);